import { parseAustralianDate } from './dates';
import { matchedHeaders, registerFields } from './headers';
import {
  DATE_FIELDS,
  friendlyEnum,
  REQUIRED_FIELDS,
  SCREENING_STATUSES,
  SCREENING_TYPES,
} from './registers';
import type { ImportIssue, ImportRegister, ImportRow, RowStatus } from './types';

function addIssue(
  issues: ImportIssue[],
  field: string,
  code: string,
  message: string,
  level?: 'INFO',
): void {
  issues.push({ field, code, message, level });
}

function resultStatus(issues: ImportIssue[]): RowStatus {
  const errorCodes = new Set([
    'REQUIRED',
    'INVALID_DATE',
    'UNKNOWN_VALUE',
    'DUPLICATE_IN_FILE',
    'COLUMN_COUNT_MISMATCH',
    'FUTURE_DATE',
    'EXPIRY_BEFORE_COMPLETION',
    'NOTES_TOO_LONG',
    'EVIDENCE_REFERENCE_TOO_LONG',
  ]);
  return issues.some((issue) => errorCodes.has(issue.code))
    ? 'ERROR'
    : issues.some((issue) => issue.level !== 'INFO')
      ? 'WARNING'
      : 'OK';
}

/** `today` is supplied by the caller as the tenant-local ISO business date. */
export function validateRegister(
  parsed: string[][],
  register: ImportRegister,
  today: string,
): { rows: ImportRow[]; unknownColumns: string[] } {
  const { fields, unknown } = matchedHeaders(parsed[0] ?? [], register);
  const permittedFields = registerFields(register);
  const externalKeys = new Set<string>();
  const names = new Set<string>();
  const trainingOccurrences = new Set<string>();
  const rows = parsed.slice(1).map((cells, offset): ImportRow => {
    const issues: ImportIssue[] = [];
    const payload: Record<string, string | boolean> = {};
    if (cells.length !== fields.length)
      addIssue(
        issues,
        'row',
        'COLUMN_COUNT_MISMATCH',
        'This row does not have the same number of columns as the header.',
      );
    fields.forEach((field, index) => {
      const value = cells[index]?.trim();
      if (permittedFields.has(field) && value)
        payload[field] = field === 'confirm_duplicate' ? value.toLowerCase() === 'true' : value;
    });
    for (const field of REQUIRED_FIELDS[register]) {
      if (!payload[field]) addIssue(issues, field, 'REQUIRED', 'This field is required.');
    }
    if (
      (register === 'SCREENINGS' ||
        register === 'TRAINING' ||
        register === 'POLICY_ACKNOWLEDGEMENTS') &&
      !payload.worker_external_key &&
      !(payload.first_name && payload.last_name)
    ) {
      addIssue(
        issues,
        'worker_external_key',
        'REQUIRED',
        'Provide a worker external key, or both first and last name.',
      );
    }
    for (const field of DATE_FIELDS) {
      if (typeof payload[field] !== 'string') continue;
      const normalised = parseAustralianDate(payload[field]);
      if (!normalised)
        addIssue(issues, field, 'INVALID_DATE', 'Enter a full Australian day-first or ISO date.');
      else payload[field] = normalised;
    }
    if (register === 'WORKERS' && typeof payload.person_type === 'string') {
      const normalised = friendlyEnum(payload.person_type, [
        'WORKER',
        'RESPONSIBLE_PERSON',
        'BOTH',
      ]);
      if (!normalised)
        addIssue(
          issues,
          'person_type',
          'UNKNOWN_VALUE',
          'Choose Worker, Responsible person, or Both.',
        );
      else payload.person_type = normalised;
    }
    if (register === 'SCREENINGS') {
      for (const [field, values, message] of [
        ['screening_type', SCREENING_TYPES, 'Choose a recognised screening type.'],
        ['status', SCREENING_STATUSES, 'Choose a recognised screening status.'],
      ] as const) {
        if (typeof payload[field] !== 'string') continue;
        const normalised = friendlyEnum(payload[field], values);
        if (!normalised) addIssue(issues, field, 'UNKNOWN_VALUE', message);
        else payload[field] = normalised;
      }
      if (typeof payload.status === 'string')
        addIssue(
          issues,
          'status',
          'AWAITING_VERIFICATION',
          'Imported as awaiting verification; your recorded status is kept as a note.',
          'INFO',
        );
    }
    if (register === 'TRAINING') {
      if (typeof payload.completed_on === 'string' && payload.completed_on > today)
        addIssue(
          issues,
          'completed_on',
          'FUTURE_DATE',
          'The completion date cannot be in the future.',
        );
      if (
        typeof payload.completed_on === 'string' &&
        typeof payload.expires_on === 'string' &&
        payload.expires_on < payload.completed_on
      )
        addIssue(
          issues,
          'expires_on',
          'EXPIRY_BEFORE_COMPLETION',
          'The expiry date cannot be before the completion date.',
        );
      if (!payload.certificate_number)
        addIssue(
          issues,
          'certificate_number',
          'MISSING_CERTIFICATE',
          'Certificate number is missing.',
        );
      if (typeof payload.notes === 'string' && payload.notes.length > 200)
        addIssue(issues, 'notes', 'NOTES_TOO_LONG', 'Notes must be 200 characters or fewer.');
      const worker = String(
        payload.worker_external_key ?? `${payload.first_name ?? ''}|${payload.last_name ?? ''}`,
      )
        .trim()
        .toLowerCase();
      const occurrence = `${worker}|${String(payload.training_name ?? '')
        .trim()
        .toLowerCase()}|${String(payload.completed_on ?? '')}`;
      if (worker !== '|' && trainingOccurrences.has(occurrence))
        addIssue(
          issues,
          'training_name',
          'POSSIBLE_DUPLICATE',
          'Another row in this file records the same worker, training, and completion date.',
        );
      trainingOccurrences.add(occurrence);
    }
    if (register === 'POLICY_ACKNOWLEDGEMENTS') {
      if (typeof payload.acknowledged_on === 'string' && payload.acknowledged_on > today)
        addIssue(
          issues,
          'acknowledged_on',
          'FUTURE_DATE',
          'The acknowledgement date cannot be in the future.',
        );
      if (typeof payload.policy_version === 'string' && !/^[1-9]\d*$/.test(payload.policy_version))
        addIssue(
          issues,
          'policy_version',
          'UNKNOWN_VALUE',
          'Enter a whole positive policy version.',
        );
      if (typeof payload.evidence_reference === 'string' && payload.evidence_reference.length > 120)
        addIssue(
          issues,
          'evidence_reference',
          'EVIDENCE_REFERENCE_TOO_LONG',
          'Evidence reference must be 120 characters or fewer.',
        );
      if (typeof payload.notes === 'string' && payload.notes.length > 200)
        addIssue(issues, 'notes', 'NOTES_TOO_LONG', 'Notes must be 200 characters or fewer.');
      addIssue(
        issues,
        'acknowledged_on',
        'RECORDED_FROM_IMPORT',
        'Recorded from import as provider-held evidence; it is not an acknowledgement made in Statura.',
        'INFO',
      );
    }
    if (register === 'WORKERS' && typeof payload.external_person_key === 'string') {
      const key = payload.external_person_key.toLowerCase();
      if (externalKeys.has(key))
        addIssue(
          issues,
          'external_person_key',
          'DUPLICATE_IN_FILE',
          'This external person key is duplicated in this file.',
        );
      externalKeys.add(key);
    }
    if (payload.first_name && payload.last_name) {
      const name = `${String(payload.first_name).toLowerCase()}|${String(payload.last_name).toLowerCase()}`;
      if (names.has(name))
        addIssue(
          issues,
          'first_name',
          'POSSIBLE_DUPLICATE',
          'Another row in this file has the same name.',
        );
      names.add(name);
    }
    if (register === 'WORKERS' && !payload.date_of_birth)
      addIssue(issues, 'date_of_birth', 'MISSING_DOB', 'Date of birth is missing.');
    if (register === 'SCREENINGS' && !payload.reference_number)
      addIssue(issues, 'reference_number', 'MISSING_REFERENCE', 'Reference number is missing.');
    if (typeof payload.expires_on === 'string' && payload.expires_on < today)
      addIssue(issues, 'expires_on', 'EXPIRED', 'The expiry date is in the past.');
    if (typeof payload.email === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email))
      addIssue(issues, 'email', 'EMAIL_FORMAT', 'Check the email address format.');
    if (typeof payload.phone === 'string' && !/^[+()\d .-]{6,}$/.test(payload.phone))
      addIssue(issues, 'phone', 'PHONE_FORMAT', 'Check the phone number format.');
    return { rowNumber: offset + 2, status: resultStatus(issues), issues, payload };
  });
  return { rows, unknownColumns: unknown };
}
